/* First-Time Buyer Guide — full body content (15 sections).
   Single source of truth: edit copy HERE; FirstTimeBuyer.jsx imports it. */
module.exports = [
  {
    id: "overview",
    h: "Buying a home in the UK — the full picture",
    intro:
      "Buying your first home is one of the largest financial decisions you'll make. In the UK the process has several distinct stages — getting financially ready, securing an Agreement in Principle, finding a property, making an offer, applying for a mortgage, instructing a conveyancer, commissioning a survey, exchanging contracts and finally completing.",
    paragraphs: [
      "This guide walks through every stage in plain English: deposit and credit prep, schemes you may qualify for, mortgage applications, conveyancing, surveys, costs to budget for, and what happens on completion day.",
      "The buying process in Scotland is materially different — offers there are usually made through a solicitor and become binding much earlier. We flag Scotland-specific points where they matter; a stand-alone Scotland guide is planned.",
      "MortgagePath does not give regulated mortgage advice. The aim is to help you arrive at a broker or solicitor conversation already understanding the language.",
    ],
  },

  {
    id: "ready",
    h: "1. Getting financially ready",
    intro:
      "Before you start viewing properties, it typically helps to have your finances in order. Lenders often consider the picture in the months beforehand, so preparation tends to pay off.",
    bullets: [
      "Build your deposit. UK mainstream lenders typically accept deposits from 5% of the property price, though 10–15% generally opens more competitive products.",
      "Check your credit reports. The three main UK credit reference agencies are Experian, Equifax and TransUnion. You can access free reports through services such as ClearScore, Credit Karma and Check My File. Look for missed payments, hard searches, electoral roll status and any old addresses still on file.",
      "Manage credit utilisation. Lenders can look at how much of your available credit you're using — keeping balances well below the available limit in the months before applying is often considered helpful.",
      "Be cautious with new credit. Taking out a car loan, buy-now-pay-later or new credit cards in the months before applying may affect the affordability calculation — every search and new line of credit is visible.",
      "Keep bank statements tidy. Many lenders review 2–3 months of statements as part of underwriting. Frequent gambling transactions, unarranged overdrafts, or unexplained large transfers can lead to further questions.",
      "Employment history. Criteria vary by lender and applicant — many prefer to see settled employment, and self-employed applicants are typically asked for 2 years of accounts or SA302s, though some lenders will consider shorter track records.",
    ],
  },

  {
    id: "schemes",
    h: "2. Government schemes and savings products",
    intro:
      "Several UK schemes exist to help first-time buyers. They change over time, so always confirm current rules on gov.uk before relying on them.",
    items: [
      {
        h: "Lifetime ISA (LISA)",
        b: "Save up to £4,000 per tax year and the government adds a 25% bonus (up to £1,000 a year). Funds can be used towards a first home up to £450,000 in any part of the UK. You must have held the LISA for at least 12 months. Withdrawing for any other reason before age 60 currently incurs a 25% penalty.",
      },
      {
        h: "Shared Ownership",
        b: "Buy a share of a property (typically between 10% and 75% of the full market value under the current model) and pay rent on the remainder, usually with a housing association. Because you only mortgage the share you buy, the deposit needed is smaller. You can normally 'staircase' (buy more shares) later. Older schemes and individual providers/leases can differ — always check the specific property's terms.",
      },
      {
        h: "First Homes scheme (England)",
        b: "New-build homes sold to local first-time buyers at a 30–50% discount versus market value. Income caps and local connection rules apply.",
      },
      {
        h: "Mortgage Guarantee Scheme",
        b: "A government-backed scheme encouraging lenders to offer 95% LTV mortgages. You apply with a normal lender — the guarantee operates behind the scenes.",
      },
      {
        h: "Scotland, Wales and Northern Ireland",
        b: "Each devolved nation has its own programmes (e.g. LIFT in Scotland, Help to Buy Wales, Co-Ownership NI). Eligibility and property price caps differ — check the relevant national portal.",
      },
    ],
  },

  {
    id: "borrow",
    h: "3. How much could I borrow?",
    intro:
      "Most UK lenders work out a maximum loan as a multiple of your gross household income, then adjust for outgoings. Criteria vary by lender and by applicant.",
    paragraphs: [
      "As a rough rule of thumb, lenders typically apply income multiples between 4× and 4.75× combined gross income. Some lenders go higher (5–5.5×) for higher earners, professionals (medics, lawyers, accountants), or specific schemes; others go lower where there are dependants or significant credit commitments.",
      "After the income multiple, lenders run an affordability assessment that typically subtracts modelled household bills, credit commitments, childcare, and a stress-tested mortgage payment (often based on a rate above the actual product rate). The exact rules vary between lenders.",
    ],
    bullets: [
      "Salary, regular overtime, shift allowances, and bonuses can usually be included — how much of each counts varies by lender and how regular the payment is.",
      "Self-employed income is normally averaged over 2 years of tax returns or company accounts; some lenders will consider a shorter track record.",
      "Benefits such as Child Benefit and tax credits are accepted by many lenders, but policies vary.",
      "Pension income, rental income and trust income each have specific rules — a broker can map which lenders treat each favourably.",
    ],
    affiliate: {
      id: "broker",
      label: "Mortgage support",
      title: "Expert mortgage advice that won't cost you a penny",
      desc: "Whether you're buying your first home, moving house or looking for a new mortgage deal, L&C can help. Get expert mortgage advice, compare deals from across the market and receive support every step of the way.",
      href: "https://www.landc.co.uk/partners/mortgagepath-uk",
      cta: "Get mortgage help",
      disclosure: "Your home or property may be repossessed if you do not keep up repayments on your mortgage. MortgagePath commission disclosure: If you complete a mortgage through L&C after using our link, MortgagePath receives a commission from L&C. The amount is calculated as a percentage of the commission L&C receives from the lender. This does not increase what you pay.",
      external: true,
    },
  },

  {
    id: "aip",
    h: "4. Agreement in Principle (AIP)",
    intro:
      "An Agreement in Principle — sometimes called a Decision in Principle or Mortgage in Principle — is a non-binding indication from a lender that they may be willing to lend a certain amount, based on a soft credit check and the figures you've shared.",
    paragraphs: [
      "An AIP is not a mortgage offer. It usually lasts 30–90 days and exists to help estate agents and sellers take your offer seriously. The full mortgage application that follows includes a hard credit search, document evidence, and underwriter review.",
      "If a lender or broker uses a soft search at the AIP stage, your credit file should not be affected. Always ask whether the search will be soft or hard before consenting.",
    ],
  },

  {
    id: "search",
    h: "5. Finding a property and making an offer",
    intro:
      "Once your AIP is in hand and you understand your budget, the property search begins. UK buying is typically through estate agents listing on Rightmove, Zoopla and OnTheMarket.",
    bullets: [
      "View properties more than once where possible — different times of day reveal different things (light, noise, parking, neighbours).",
      "Ask the agent about lease length (if leasehold), service charge / ground rent, EPC rating, council tax band, and any planned works.",
      "Offers in England, Wales and Northern Ireland are not legally binding until exchange. In Scotland the process is materially different — offers are usually submitted through your solicitor, and an accepted offer under 'missives' becomes binding much earlier in the process. Consider the Scottish route (Home Report, missives, date of entry) separately.",
      "If your offer is accepted (in England, Wales or NI), the property should be marked 'Sold Subject to Contract' (SSTC). The clock now starts on conveyancing, mortgage application and survey.",
    ],
  },

  {
    id: "application",
    h: "6. The full mortgage application",
    intro:
      "With an offer accepted, you move from AIP to a full mortgage application. This is where the underwriter reviews everything in detail.",
    bullets: [
      "Expect to be asked for: photo ID, proof of address, recent payslips and bank statements, P60, and SA302s (if self-employed). Exact document requirements vary by lender.",
      "The lender will typically run a hard credit search at this stage.",
      "The lender will also instruct a property valuation — see the Valuation section below — which is separate from your survey.",
      "If approved, you receive a formal mortgage offer (usually valid 3–6 months). Read it carefully — once you exchange contracts, you're committed.",
    ],
  },

  {
    id: "valuation",
    h: "7. The lender's valuation (this is not a survey)",
    intro:
      "The lender's valuation is a brief inspection (sometimes a desktop check) carried out for the lender, to confirm the property is worth at least the amount they're being asked to lend against.",
    paragraphs: [
      "It is performed for the lender, not for you. It produces a one-line opinion of value, not a structural assessment, and you may never see the full report. Do not rely on the lender's valuation to tell you about the condition of the property — that is what a survey is for.",
      "Many lenders fund a basic valuation as part of the product; others charge a fee. A 'down-valuation' (lender values the property below the agreed sale price) can mean renegotiating the price, putting in extra deposit, or — occasionally — losing the deal.",
    ],
  },

  /* ---------- CONVEYANCING — its own focused section ---------- */
  {
    id: "conveyancing",
    h: "8. Conveyancing — the legal side",
    intro:
      "Conveyancing is the legal work that transfers ownership of the property from the seller to you. It is completely separate from the survey, and is carried out by a licensed conveyancer or a solicitor who is qualified to do property work.",
    paragraphs: [
      "In England, Wales and Northern Ireland your conveyancer represents your legal interest — the seller has their own conveyancer, and the two sides exchange information, raise enquiries, conduct local searches, agree the contract, exchange contracts on an agreed date, and finally complete the transfer of funds and ownership.",
      "In Scotland the process runs differently: solicitors are typically involved from the offer stage onwards, most properties come with a seller-supplied Home Report (survey, property questionnaire and energy report) up front, and 'missives' are the letters between solicitors that form the binding contract before completion (the 'date of entry').",
    ],
    items: [
      {
        h: "Property searches",
        b: "Standard searches typically include the Local Authority search (planning, road schemes, building regs), Environmental search (contamination, flood risk, radon), Water & Drainage search, and a Chancel Repair search where relevant. New-build or leasehold properties often need additional searches.",
      },
      {
        h: "Title checks and enquiries",
        b: "Your conveyancer reviews the Land Registry title (or Registers of Scotland title in Scotland), the seller's property information forms (TA6, TA10 in England/Wales; Home Report Property Questionnaire in Scotland), and any leasehold documents (lease length, ground rent, service charge accounts, planned major works). They raise enquiries with the seller's solicitor on anything unclear.",
      },
      {
        h: "Mortgage representation",
        b: "Most lenders require your conveyancer to also represent them. The conveyancer makes sure the legal title is acceptable to the lender and reports any defects (e.g. short lease, restrictive covenants, lack of building regulations sign-off).",
      },
      {
        h: "Stamp Duty Land Tax (or equivalent)",
        b: "Your conveyancer calculates and submits the SDLT return (England & NI), LBTT return (Scotland) or LTT return (Wales). First-time buyer reliefs may apply — see our Stamp Duty & Property Tax Calculator, or confirm current thresholds on gov.uk / revenue.scot / gov.wales.",
      },
      {
        h: "Exchange of contracts (or conclusion of missives in Scotland)",
        b: "In England/Wales/NI, both sides sign identical contracts, the deposit (usually around 10% of the price) is transferred, and the transaction becomes legally binding. In Scotland, conclusion of missives has a similar binding effect. A completion date (or 'date of entry' in Scotland) is fixed — often 1–4 weeks later.",
      },
      {
        h: "Completion",
        b: "Your conveyancer sends the balance of funds to the seller's conveyancer, the keys are released by the estate agent, the property is registered into your name at the Land Registry (or Registers of Scotland), and SDLT/LBTT/LTT is paid.",
      },
    ],
    bullets: [
      "Typical conveyancing cost in 2026: ~£900–£1,800 in legal fees plus disbursements (searches usually £250–£500, Land Registry, SDLT submission).",
      "Choose a firm regulated by the SRA (Solicitors Regulation Authority) or the CLC (Council for Licensed Conveyancers) — or, in Scotland, the Law Society of Scotland.",
      "Online vs. local: online firms are often cheaper and quicker on simple freehold transactions; complex chains, leasehold, Scottish transactions or unusual properties can benefit from a local solicitor.",
    ],
    affiliate: {
      id: "conveyancer",
      label: "Conveyancing quote",
      title: "Need a conveyancing quote?",
      desc: "Get a no-obligation quote from MortgagePath's selected conveyancing partner.",
      href: "/conveyancing-quote?source=guide_ftb_conveyancing",
      cta: "Request a conveyancing quote",
      disclosure: "MortgagePath is not a conveyancer and does not provide conveyancing advice. If you submit the form, you'll be asked to confirm consent on a final screen where we clearly name our selected conveyancing provider before your details are shared. MortgagePath may receive a referral fee if you choose to proceed.",
      internal: true,
    },
  },

  /* ---------- SURVEYS — fully separate section ---------- */
  {
    id: "surveys",
    h: "9. Surveys — checking the property's condition",
    intro:
      "A survey is a physical inspection of the property by a qualified surveyor — typically RICS-regulated. It is paid for by you, performed for you, and provides an independent assessment of the property's condition. It is completely separate from the lender's valuation and from the legal conveyancing work.",
    paragraphs: [
      "The Royal Institution of Chartered Surveyors (RICS) defines three standard levels of survey. The appropriate survey depends on the property and your circumstances — its age, condition, construction and any specific concerns you have. Your surveyor can help you choose. New-build properties often have a separate 'snagging' inspection instead.",
      "The survey quote form on MortgagePath shows the customer-facing options currently available. Below is a short explanation of each, matching the labels on the form so you can move straight from reading about them to requesting a quote.",
    ],
    items: [
      {
        h: "RICS Level 1 Home Survey (Condition Report)",
        b: "A short visual inspection using a simple traffic-light rating for each element. No advice on repairs or valuation. May be suitable for newer homes in obviously good condition. Indicative cost: £250–£500.",
      },
      {
        h: "RICS Level 2 Home Survey (previously known as a HomeBuyer Report)",
        b: "A more detailed visual inspection covering all major elements, identifying defects that may affect value, with advice on repairs and maintenance. Often considered for standard, conventional homes in reasonable condition. Indicative cost: £450–£900.",
      },
      {
        h: "RICS Level 2 Home Survey with a valuation",
        b: "The Level 2 report plus a professional open-market valuation of the property. Often considered where you also want an independent view of value alongside the condition report.",
      },
      {
        h: "RICS Level 3 Home Survey (Building Survey)",
        b: "The most comprehensive option. A detailed inspection covering visible and accessible parts of the structure, with technical commentary on construction, defects and recommended repairs. Often considered for older, larger, listed, unusual or altered properties, or where a more detailed inspection is wanted. Indicative cost: £700–£1,500+.",
      },
      {
        h: "Independent Valuation",
        b: "A professional opinion of market value on its own — useful for probate, dispute or other specific purposes where a condition report isn't the main thing needed.",
      },
      {
        h: "Help to Buy / scheme-specific valuation",
        b: "A scheme-specific valuation where relevant to a particular scheme purchase.",
      },
      {
        h: "New-build snagging inspection",
        b: "Not strictly a RICS survey level. An independent inspector checks a new-build property for defects (paintwork, fittings, finish, mechanical issues) before you complete or in the first two years while the developer's warranty covers fixes. Indicative cost: £300–£600.",
      },
      {
        h: "Specialist follow-up inspections",
        b: "A surveyor may recommend specialist reports — e.g. a damp and timber report, an electrical (EICR) test, a gas safety inspection, or a structural engineer's opinion on a specific crack or movement. Budget for these where the main survey flags concerns.",
      },
    ],
    bullets: [
      "Not sure which survey you need? The quote form shows the available survey and valuation options with a short explanation of each — you can compare them and pick when you request the quote.",
      "Confirm the surveyor is RICS-regulated and has professional indemnity insurance.",
      "Reading the survey: focus on defects rated 'urgent' or 'significant'. Quotes for those works should inform any renegotiation with the seller.",
      "If the survey reveals major issues you didn't expect, options include renegotiating the price, asking the seller to fix items pre-completion, or — because nothing is binding until exchange — withdrawing.",
      "In Scotland, most sellers provide a Home Report up front, which includes a single survey. Buyers can still commission additional inspections where they want a more detailed view.",
    ],
    affiliate: {
      id: "surveyor",
      label: "Survey quote",
      title: "Need a survey quote?",
      desc: "Get a no-obligation quote from a RICS-regulated survey provider.",
      href: "/survey-quote?source=guide_ftb_survey",
      cta: "Request a survey quote",
      disclosure: "MortgagePath is not a surveyor and does not provide surveying advice. If you submit the form, you'll be asked to confirm consent on a final screen where we clearly name our selected RICS-regulated survey provider before your details are shared. MortgagePath may receive a referral fee if you choose to proceed.",
      internal: true,
    },
  },

  {
    id: "exchange",
    h: "10. Exchange of contracts (England, Wales and NI) / conclusion of missives (Scotland)",
    intro:
      "This is the legally binding moment. In England, Wales and Northern Ireland it happens at 'exchange of contracts'; in Scotland at 'conclusion of missives'.",
    paragraphs: [
      "From this moment, neither party can walk away without significant financial consequences. This is why your mortgage offer, buildings insurance, deposit funds and surveys typically need to be in place before this point — not after.",
      "You will normally need buildings insurance to start from the date of exchange (or conclusion of missives in Scotland), because legally you carry the risk on the property from this point.",
    ],
  },

  {
    id: "completion",
    h: "11. Completion day (or 'date of entry' in Scotland)",
    intro:
      "On the agreed completion date, your conveyancer sends the balance of the purchase price to the seller's conveyancer. Once funds are confirmed received, the estate agent releases the keys.",
    bullets: [
      "Keys are usually available from late morning / early afternoon on completion day.",
      "Your conveyancer registers the transfer with HM Land Registry (or Registers of Scotland) and submits the SDLT/LBTT/LTT return.",
      "Your first mortgage payment is normally due the following month, with a slightly higher first payment to cover interest from completion date to the first regular payment date.",
    ],
  },

  {
    id: "costs",
    h: "12. Costs to budget for (beyond the deposit)",
    intro:
      "Many first-time buyers under-estimate the 'extras'. Build a buffer into your savings plan.",
    bullets: [
      "Stamp Duty Land Tax (or LBTT in Scotland, LTT in Wales) — first-time buyer relief may apply up to set thresholds. Use our Stamp Duty & Property Tax Calculator, or confirm current rates on the relevant government portal.",
      "Conveyancing fees and disbursements — typically £1,200–£2,500 all-in.",
      "Survey — £250–£1,500+ depending on level.",
      "Mortgage arrangement / product fee — sometimes added to the loan; often £495–£1,500.",
      "Mortgage broker fee — some brokers are fee-free, others charge ~£295–£995.",
      "Removals — £400–£1,500 typically, more for long-distance or large homes.",
      "Buildings insurance — required from exchange (or conclusion of missives in Scotland). Contents insurance recommended.",
      "Immediate setup costs — utilities, council tax, broadband, locks/keys, basic furniture and white goods.",
    ],
    affiliate: {
      id: "stampduty",
      label: "Free tool",
      title: "Want to know how much property tax you may pay?",
      desc: "Use our free Stamp Duty & Property Tax Calculator to estimate SDLT, LTT or LBTT on your purchase.",
      href: "/stamp-duty-calculator?source=guide_ftb_costs",
      cta: "Calculate stamp duty",
      internal: true,
    },
  },

  {
    id: "after",
    h: "13. After you move in",
    intro:
      "The job isn't quite done when the kettle is on. A few admin tasks in the first weeks save trouble later.",
    bullets: [
      "Take meter readings on completion day and register with utility suppliers.",
      "Inform your local council you've moved in (council tax).",
      "Register on the electoral roll at your new address.",
      "Update DVLA (driving licence and V5C for any vehicles), HMRC, your employer, banks, GP and insurance providers.",
      "Test smoke alarms, CO alarms and locate the stopcock, fuse board and gas meter.",
      "Save digital copies of the TA6/TA10 forms (or Home Report in Scotland), completion statement, mortgage offer and survey for future reference.",
    ],
  },

  {
    id: "energy",
    h: "14. Energy and ongoing running costs",
    intro:
      "Running costs vary widely between properties. Check the EPC rating, the heating system age, insulation levels and window quality before you offer.",
    paragraphs: [
      "Most UK homes use gas combi or system boilers; many newer homes use air-source heat pumps. EPC bands run A (most efficient) to G (least efficient); the EPC certificate also gives indicative annual heating costs and recommended improvements.",
      "Switching energy supplier on day one is one of the easier wins. Compare tariffs on gov.uk or an Ofgem-accredited comparison site.",
    ],
  },

  {
    id: "how",
    h: "15. How MortgagePath helps",
    intro:
      "We don't sell mortgages and we don't give regulated advice. We help you arrive at a broker conversation already understanding the figures, the journey, and the language.",
    bullets: [
      "Use the free Mortgage Snapshot to see how your numbers sit against typical lender bands.",
      "Use the calculator to explore monthly repayments and overpayment scenarios.",
      "Read the FAQs for plain-English answers to the questions buyers most often ask.",
      "If you'd like regulated advice, you can follow our optional link to a qualified, regulated broker — entirely optional. You'll be leaving MortgagePath and dealing directly with the broker.",
    ],
  },
];
